/**
 * Motion Utilities - Framer Motion integration for ConversaLang
 *
 * Provides:
 * - Motion component wrappers (MotionDiv, MotionButton, etc.)
 * - Shared animation variants for buttons, cards, pages
 * - useReducedMotion hook for accessibility
 * - AnimatePresence wrapper for exit animations
 *
 * @example
 * ```tsx
 * import { MotionCard, cardHoverVariants } from '@/components/ui/motion';
 *
 * <MotionCard
 *   variants={cardHoverVariants}
 *   whileHover="hover"
 *   initial="initial"
 * >
 *   Content
 * </MotionCard>
 * ```
 */

import {
  motion,
  AnimatePresence,
  type HTMLMotionProps,
  type Variants,
} from 'framer-motion';
import { useEffect, useState } from 'react';

// ============================================================================
// MOTION COMPONENT WRAPPERS
// ============================================================================

export const MotionDiv = motion.div;
export const MotionButton = motion.button;
export const MotionSpan = motion.span;
export const MotionLi = motion.li;
export const MotionUl = motion.ul;
export const MotionNav = motion.nav;
export const MotionSection = motion.section;
export const MotionArticle = motion.article;
export const MotionMain = motion.main;

// Re-export AnimatePresence for convenience
export { AnimatePresence };

// ============================================================================
// ACCESSIBILITY HOOK
// ============================================================================

/**
 * Hook to detect user's reduced motion preference
 * Returns true if user prefers reduced motion
 *
 * @example
 * ```tsx
 * const prefersReducedMotion = useReducedMotion();
 * const variants = prefersReducedMotion ? reducedVariants : fullVariants;
 * ```
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
}

// ============================================================================
// BUTTON VARIANTS
// ============================================================================

/**
 * Button animation variants - subtle scale on hover/tap
 * Spring physics for responsive feel
 */
export const buttonVariants: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.02 },
  tap: { scale: 0.98 },
};

export const buttonTransition = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 17,
};

/**
 * Button variants with no motion (for reduced motion preference)
 */
export const buttonVariantsReduced: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1 },
  tap: { scale: 1 },
};

// ============================================================================
// CARD VARIANTS
// ============================================================================

/**
 * Card hover animation - subtle lift with shadow
 * Tween for controlled, elegant movement
 */
export const cardHoverVariants: Variants = {
  initial: {
    y: 0,
    boxShadow: '0 1px 3px oklch(0 0 0 / 0.05), 0 1px 2px oklch(0 0 0 / 0.1)'
  },
  hover: {
    y: -4,
    boxShadow: '0 8px 30px oklch(0 0 0 / 0.12)'
  },
};

export const cardTransition = {
  type: 'tween' as const,
  duration: 0.2,
  ease: [0, 0, 0.58, 1] as const, // easeOut cubic-bezier
};

/**
 * Card variants with no motion (for reduced motion preference)
 */
export const cardHoverVariantsReduced: Variants = {
  initial: {
    y: 0,
    boxShadow: '0 1px 3px oklch(0 0 0 / 0.05), 0 1px 2px oklch(0 0 0 / 0.1)'
  },
  hover: {
    y: 0,
    boxShadow: '0 4px 15px oklch(0 0 0 / 0.1)'
  },
};

/**
 * Interactive card - more pronounced hover for clickable cards
 */
export const interactiveCardVariants: Variants = {
  initial: {
    scale: 1,
    y: 0,
  },
  hover: {
    scale: 1.02,
    y: -2,
  },
  tap: {
    scale: 0.98,
    y: 0,
  },
};

// ============================================================================
// PAGE TRANSITION VARIANTS
// ============================================================================

/**
 * Page transition - fade in with subtle upward movement
 */
export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  enter: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: -10,
  },
};

export const pageTransition = {
  type: 'tween' as const,
  duration: 0.25,
  ease: [0.25, 0.1, 0.25, 1], // Custom ease-out
};

/**
 * Page variants with reduced motion - instant opacity change
 */
export const pageVariantsReduced: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1 },
  exit: { opacity: 0 },
};

export const pageTransitionReduced = {
  duration: 0.1,
};

// ============================================================================
// LIST/STAGGER VARIANTS
// ============================================================================

/**
 * Container for staggered children animations
 */
export const staggerContainerVariants: Variants = {
  initial: {},
  enter: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
};

/**
 * Individual item in a staggered list
 */
export const staggerItemVariants: Variants = {
  initial: {
    opacity: 0,
    y: 10
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.15,
    },
  },
};

// ============================================================================
// SUCCESS/CELEBRATION VARIANTS
// ============================================================================

/**
 * Success pulse animation for correct answers
 * Subtle scale + color emphasis
 */
export const successPulseVariants: Variants = {
  initial: { scale: 1 },
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 0.3,
      times: [0, 0.5, 1],
      ease: 'easeInOut',
    },
  },
};

/**
 * Milestone celebration - more pronounced animation
 * Only use for significant achievements
 */
export const milestoneCelebrationVariants: Variants = {
  initial: {
    scale: 1,
    rotate: 0
  },
  celebrate: {
    scale: [1, 1.1, 1],
    rotate: [0, -2, 2, 0],
    transition: {
      duration: 0.4,
      times: [0, 0.3, 0.6, 1],
      ease: 'easeInOut',
    },
  },
};

// ============================================================================
// FADE VARIANTS
// ============================================================================

/**
 * Simple fade in/out
 */
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1 },
  exit: { opacity: 0 },
};

export const fadeTransition = {
  duration: 0.15,
};

/**
 * Fade with scale (for modals/popovers)
 */
export const fadeScaleVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95
  },
  enter: {
    opacity: 1,
    scale: 1
  },
  exit: {
    opacity: 0,
    scale: 0.95
  },
};

export const fadeScaleTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 25,
};

// ============================================================================
// SLIDE VARIANTS
// ============================================================================

/**
 * Slide in from right (for sidebars/drawers)
 */
export const slideInRightVariants: Variants = {
  initial: { x: '100%', opacity: 0 },
  enter: { x: 0, opacity: 1 },
  exit: { x: '100%', opacity: 0 },
};

/**
 * Slide in from left
 */
export const slideInLeftVariants: Variants = {
  initial: { x: '-100%', opacity: 0 },
  enter: { x: 0, opacity: 1 },
  exit: { x: '-100%', opacity: 0 },
};

/**
 * Slide in from bottom (for sheets/toasts)
 */
export const slideInBottomVariants: Variants = {
  initial: { y: '100%', opacity: 0 },
  enter: { y: 0, opacity: 1 },
  exit: { y: '100%', opacity: 0 },
};

export const slideTransition = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * Motion Card wrapper with hover animation
 * Automatically respects reduced motion preference
 */
export interface MotionCardProps extends HTMLMotionProps<'div'> {
  interactive?: boolean;
}

export function MotionCard({
  children,
  interactive = false,
  ...props
}: MotionCardProps) {
  const prefersReducedMotion = useReducedMotion();

  const variants = prefersReducedMotion
    ? cardHoverVariantsReduced
    : interactive
      ? interactiveCardVariants
      : cardHoverVariants;

  return (
    <MotionDiv
      variants={variants}
      initial="initial"
      whileHover="hover"
      whileTap={interactive ? "tap" : undefined}
      transition={cardTransition}
      {...props}
    >
      {children}
    </MotionDiv>
  );
}

/**
 * Page transition wrapper
 * Wrap lazy-loaded page components for smooth transitions
 */
export interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export function PageTransition({ children, className }: PageTransitionProps) {
  const prefersReducedMotion = useReducedMotion();
  const [hasAnimated, setHasAnimated] = useState(false);

  return (
    <MotionDiv
      variants={prefersReducedMotion ? pageVariantsReduced : pageVariants}
      initial={hasAnimated ? false : "initial"}
      animate="enter"
      exit="exit"
      transition={prefersReducedMotion ? pageTransitionReduced : pageTransition}
      className={className}
      onAnimationComplete={() => setHasAnimated(true)}
    >
      {children}
    </MotionDiv>
  );
}

/**
 * Staggered list wrapper
 * Wrap a list container to stagger children animations
 */
export interface StaggeredListProps {
  children: React.ReactNode;
  className?: string;
}

export function StaggeredList({
  children,
  className,
}: StaggeredListProps) {
  const prefersReducedMotion = useReducedMotion();
  const [hasAnimated, setHasAnimated] = useState(false);

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <MotionDiv
      variants={staggerContainerVariants}
      initial={hasAnimated ? false : "initial"}
      animate="enter"
      exit="exit"
      className={className}
      onAnimationComplete={() => setHasAnimated(true)}
    >
      {children}
    </MotionDiv>
  );
}

/**
 * Staggered list item
 */
export interface StaggeredItemProps {
  children: React.ReactNode;
  className?: string;
}

export function StaggeredItem({
  children,
  className,
}: StaggeredItemProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <MotionDiv
      variants={staggerItemVariants}
      className={className}
    >
      {children}
    </MotionDiv>
  );
}
